import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Cpu, Save, Loader2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { tr } from "@/lib/i18n";

// Provider order for optgroup rendering — keep Claude first because it's
// the most-used option here (prompt-caching + long context) and the one
// most likely to be picked for main writing tasks.
const PROVIDER_LABEL = {
  anthropic: "Anthropic Claude",
  openai: "OpenAI",
  gemini: "Google Gemini",
};

function groupModelsByProvider(models) {
  const groups = {};
  for (const m of models || []) {
    (groups[m.provider] = groups[m.provider] || []).push(m);
  }
  return groups;
}

export default function LlmModelsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [dirty, setDirty] = useState({}); // { task_key: {provider, model} }

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-llm-models"],
    queryFn: () => api.adminGetLlmModels(),
    enabled: user?.role === "admin",
  });

  // Seed the working copy from the server response once, so the user's
  // in-flight edits aren't clobbered by React Query refetches.
  useEffect(() => {
    if (data?.tasks && Object.keys(dirty).length === 0) {
      setDirty(data.tasks);
    }
  }, [data?.tasks, dirty]);

  const saveMutation = useMutation({
    mutationFn: (tasks) => api.adminPutLlmModels(tasks),
    onSuccess: (resp) => {
      toast.success(tr("LLM model routing saved"));
      qc.setQueryData(["admin-llm-models"], (prev) =>
        prev ? { ...prev, tasks: resp.tasks } : prev
      );
      setDirty(resp.tasks);
    },
    onError: (e) =>
      toast.error(
        e?.response?.data?.detail || e.message || tr("Failed to save")
      ),
  });

  if (user?.role !== "admin") {
    return (
      <div className="p-10">
        <div className="font-serif-display text-2xl">{tr("Not authorized")}</div>
        <div className="text-sm text-[var(--ink-muted)] mt-2">{tr("Only admins can access this page.")}</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-10 flex items-center gap-2 text-[var(--ink-muted)]">
        <Loader2 className="w-4 h-4 animate-spin" />{tr("Loading…")}</div>
    );
  }
  if (error) {
    return (
      <div className="p-10 text-red-600">{tr("Failed to load model config: {message}", { message: error.message })}
      </div>
    );
  }

  const catalog = data?.catalog || {};
  const tasks = catalog.tasks || [];
  const modelsText = catalog.models_text || [];
  const modelsImage = catalog.models_image || [];
  const modelsOcr = catalog.models_ocr || [];

  const pickList = (kind) =>
    kind === "text" ? modelsText : kind === "image" ? modelsImage : modelsOcr;

  const setForTask = (taskKey, value) => {
    // value = "provider|model"
    const [provider, model] = value.split("|", 2);
    setDirty((prev) => ({ ...prev, [taskKey]: { provider, model } }));
  };

  const hasChanges = (() => {
    if (!data?.tasks) return false;
    return tasks.some((t) => {
      const cur = data.tasks[t.key] || t.default;
      const next = dirty[t.key] || cur;
      return cur.provider !== next.provider || cur.model !== next.model;
    });
  })();

  const resetToDefaults = () => {
    const next = {};
    for (const t of tasks) next[t.key] = { ...t.default };
    setDirty(next);
    toast.info(tr("Reset to defaults — click Save to persist"));
  };

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-[var(--border)] bg-white sticky top-0 z-10">
        <div className="px-4 sm:px-10 py-5 sm:py-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="overline flex items-center gap-2">
              <Cpu className="w-3 h-3" />{tr("Admin")}</div>
            <h1 className="font-serif-display text-3xl sm:text-4xl tracking-tight leading-tight mt-1">{tr("LLM Models")}</h1>
            <div className="text-xs sm:text-sm text-[var(--ink-muted)] mt-1 max-w-2xl">{tr("Choose which model runs each task in Newsdesk. Changes take effect on the next LLM call — no restart required. Applies globally to every property.")}</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="llm-models-reset"
              onClick={resetToDefaults}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs border border-[var(--border)] hover:border-[var(--ink)] hover:bg-[var(--secondary)]"
            >
              <RefreshCw className="w-3.5 h-3.5" />{tr("Reset to defaults")}</button>
            <button
              data-testid="llm-models-save"
              disabled={!hasChanges || saveMutation.isPending}
              onClick={() => saveMutation.mutate(dirty)}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs bg-[var(--ink)] text-white hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saveMutation.isPending ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />{tr("Saving…")}</>
              ) : (
                <>
                  <Save className="w-3.5 h-3.5" />{tr("Save changes")}</>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-10 py-6 sm:py-8 flex-1 overflow-auto">
        <div className="max-w-4xl">
          <div className="border border-[var(--border)] bg-white">
            {tasks.map((t, i) => {
              const cur = dirty[t.key] || data.tasks[t.key] || t.default;
              const value = `${cur.provider}|${cur.model}`;
              const models = pickList(t.kind);
              const groups = groupModelsByProvider(models);
              const isDefault =
                cur.provider === t.default.provider &&
                cur.model === t.default.model;
              return (
                <div
                  key={t.key}
                  data-testid={`llm-task-row-${t.key}`}
                  className={`grid grid-cols-1 sm:grid-cols-[2fr_3fr_auto] gap-3 sm:gap-4 items-center px-4 py-3 ${
                    i !== tasks.length - 1
                      ? "border-b border-[var(--border)]"
                      : ""
                  }`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-serif-display leading-tight">
                      {t.label}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider font-mono-display text-[var(--ink-muted)] mt-0.5">
                      {t.key} · {t.kind}
                    </div>
                  </div>
                  <select
                    data-testid={`llm-task-select-${t.key}`}
                    value={value}
                    onChange={(e) => setForTask(t.key, e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-[var(--border)] outline-none focus:border-[var(--ink)] bg-white font-mono-display"
                  >
                    {Object.entries(groups).map(([prov, list]) => (
                      <optgroup
                        key={prov}
                        label={PROVIDER_LABEL[prov] || prov}
                      >
                        {list.map((m) => (
                          <option
                            key={`${m.provider}|${m.model}`}
                            value={`${m.provider}|${m.model}`}
                          >
                            {m.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <div className="text-[10px] uppercase tracking-wider font-mono-display text-[var(--ink-muted)] sm:text-right">
                    {isDefault ? tr("default") : tr("custom")}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 text-xs text-[var(--ink-muted)] font-mono-display leading-relaxed">
            <div className="uppercase tracking-wider text-[10px] text-[var(--ink)] mb-1">{tr("Notes")}</div>
            <div>{tr("· Claude models keep prompt-caching (~30% cost saving on repeated system prompts).")}</div>
            <div>{tr("· OpenAI and Gemini models route through the Emergent Universal Key and are billed per token.")}</div>
            <div>{tr("· \"Article writing\" is the highest-volume task — pick a model tuned for long-form generation (Opus 4.8, Sonnet 5, or Sonnet 4.6).")}</div>
            <div>{tr("· \"Rank sources\" and \"Find more sources\" are short calls where Haiku or GPT 5.4 Mini save credits.")}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
